import { Box, Tooltip } from '@mui/material';

import { ChipInformativo } from '../../Presentaciones/components/PresentationStateChip';

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/**
 * Los tres pares de aviso del módulo, con el texto OSCURO.
 *
 * Son los que este badge ya venía usando. Se nombran y se exportan porque la
 * paleta de los chips de estado (`PALETA_ESTADO_CUIT_SVCC`) tiene el texto en el
 * tono fuerte sobre un fondo casi blanco —`#FF9900` sobre `#FFFBEA`— y en un
 * chip chico eso no se lee ni se distingue del fondo de la fila. Para avisos
 * informativos sirven éstos, que es la misma familia con el texto oscurecido.
 */
/*
 * Fondo con cuerpo y borde del mismo tono: el `#FFF7E6` anterior sobre la fila
 * blanca no se veía como chip, y el operador preguntó "qué es esto" sobre
 * "2025 · 5 sin enviar".
 */
export const AVISO_ROJO = { textColor: '#8B1A12', backgorundColor: '#FBDCDA', borderColor: '#E9A29C' };
export const AVISO_AMBAR = { textColor: '#6B4300', backgorundColor: '#FFE3A8', borderColor: '#E6B65C' };
export const AVISO_NEUTRO = {
    textColor: 'rgba(0,0,0,.6)',
    backgorundColor: '#E4E9EE',
    borderColor: '#C9D2DC'
};

/** Días restantes a partir de los cuales el aviso deja de ser discreto. */
const UMBRAL_VENCE_PRONTO = 7;

interface VigenciaBadgeProps {
    /** Fecha ISO que devuelve el backend, o null si no hay trámite que purgar. */
    fechaVencimientoEstimada?: string | null;
    /**
     * Fuerza el ocultamiento. Lo usa `DESINCRONIZADA`: ahí la SRT ya consolidó
     * y el trámite no se purga, pero el backend no conoce ese estado (se deriva
     * en el Front contra la verificación de sincronización), así que puede
     * mandar una fecha que hay que ignorar.
     */
    suprimido?: boolean;
}

/**
 * Cuánto le queda a una presentación abierta antes de que la SRT la purgue.
 *
 * Los días se calculan acá y no en el backend a propósito: si el número
 * viniera calculado del servidor, se congelaría al momento del request y
 * seguiría diciendo lo mismo con la pestaña abierta al día siguiente.
 *
 * El plazo no está confirmado con la SRT — de ahí el `~` y el "(estimado)".
 */
const VigenciaBadge = ({ fechaVencimientoEstimada, suprimido = false }: VigenciaBadgeProps) => {
    if (suprimido || !fechaVencimientoEstimada) return null;

    const vence = new Date(fechaVencimientoEstimada);
    if (Number.isNaN(vence.getTime())) return null;

    // Hacia arriba para no adelantar el vencimiento: a 12 horas de vencer
    // tiene que decir "~1 día", no "0".
    const dias = Math.ceil((vence.getTime() - Date.now()) / MS_POR_DIA);

    let etiqueta: string;
    let paleta: { textColor: string; backgorundColor: string };

    if (dias <= 0) {
        etiqueta = '⚠ Vencida (estimado)';
        paleta = AVISO_ROJO;
    } else if (dias <= UMBRAL_VENCE_PRONTO) {
        etiqueta = `⏳ Vence pronto · ~${dias} ${dias === 1 ? 'día' : 'días'}`;
        paleta = AVISO_AMBAR;
    } else {
        etiqueta = `⏳ Vigente · ~${dias} días`;
        paleta = AVISO_NEUTRO;
    }

    const fechaLegible = vence.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });

    return (
        <Tooltip
            title={`Estimado: la SRT borra los borradores sin consolidar a los 30 días sin actualización. Vencimiento estimado: ${fechaLegible}.`}
        >
            {/*
              * Usa el chip de la app en vez de una caja propia: antes tenía su
              * padding, su radio de 16px, 0.75rem (10,5px con el root en 14) y
              * peso 500, así que al lado de los chips de estado de la misma fila
              * se veía como otro elemento. `ChipInformativo` comparte el `sx` con
              * ellos.
              */}
            <Box component="span" sx={{ display: 'inline-block', cursor: 'help' }}>
                <ChipInformativo
                    label={etiqueta}
                    paleta={paleta}
                    testId="vigencia-badge"
                />
            </Box>
        </Tooltip>
    );
};

export default VigenciaBadge;
